<script lang="ts">
	import { onMount } from 'svelte';
	import { browser } from '$app/environment';

	const CONSENT_KEY = 'akonia_cookie_consent';

	let showBanner = $state(false);

	function dispatchConsentChange(consent: 'accepted' | 'declined') {
		if (!browser) return;
		window.dispatchEvent(new CustomEvent('cookie-consent-change', { detail: { consent } }));
	}

	function acceptCookies() {
		if (browser) {
			localStorage.setItem(CONSENT_KEY, 'accepted');
			showBanner = false;
			dispatchConsentChange('accepted');
		}
	}

	function declineCookies() {
		if (browser) {
			localStorage.setItem(CONSENT_KEY, 'declined');
			showBanner = false;
			dispatchConsentChange('declined');
		}
	}

	onMount(() => {
		if (browser) {
			const consent = localStorage.getItem(CONSENT_KEY);
			if (!consent) {
				setTimeout(() => showBanner = true, 1000);
			}
		}
	});
</script>

{#if showBanner}
	<div
		class="fixed bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] left-4 right-4 md:left-auto md:right-8 md:w-100 bg-ak-surface-raised rounded-lg p-4 sm:p-5 shadow-2xl animate-slide-up border border-ak-wintergreen/30 z-2147483647"
		role="dialog"
		aria-labelledby="cookie-heading"
	>
		<div class="flex items-start gap-3 sm:gap-4">
			<div class="shrink-0 w-10 h-10 rounded-full bg-ak-wintergreen/10 flex items-center justify-center">
				<svg class="w-5 h-5 text-ak-wintergreen" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
					<path stroke-linecap="round" stroke-linejoin="round" d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
				</svg>
			</div>
			<div class="flex-1">
				<h3 id="cookie-heading" class="font-display font-semibold text-base sm:text-lg mb-1">We value your privacy</h3>
				<p class="text-xs text-ak-text-secondary leading-relaxed mb-4">
					We use cookies to enhance your browsing experience, show personalized content, and analyze our traffic. Read our <a href="/privacy" class="text-ak-wintergreen hover:underline py-1">Privacy Policy</a> to learn more.
				</p>
				<div class="flex flex-wrap gap-2">
					<button
						onclick={acceptCookies}
						class="flex-1 min-w-25 px-4 py-2.5 min-h-11 bg-ak-wintergreen text-ak-deep font-semibold text-sm rounded-md hover:opacity-90 transition-opacity whitespace-nowrap touch-manipulation"
					>
						Accept All
					</button>
					<button
						onclick={declineCookies}
						class="flex-1 min-w-25 px-4 py-2.5 min-h-11 border border-ak-border-subtle bg-ak-surface text-ak-text-secondary hover:text-ak-text-primary hover:border-ak-wintergreen text-sm font-medium rounded-md transition-colors whitespace-nowrap touch-manipulation"
					>
						Decline Optional
					</button>
				</div>
			</div>
		</div>
	</div>
{/if}

<style>
	@keyframes slideUp {
		from { opacity: 0; transform: translateY(20px); }
		to { opacity: 1; transform: translateY(0); }
	}
	.animate-slide-up {
		animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
	}
</style>
